// Print-bleed ("fond perdu") geometry helpers.
//
// A print shop trims the bound book a hair inside the printed sheet. If page
// content stops exactly at the trim line, any drift in the cutter leaves a
// white sliver on the edge. The fix is "bleed": extend the page a few mm past
// the trim on every side so the blade always cuts through ink, never paper.
//
// These two pure functions express that in pdf-lib's Y-up point space:
//   - `fullBleedRect` enlarges a trim-sized media box outward by `bleedPt`.
//   - `extendSlotsToBleed` pushes only the photo slots that actually touch a
//     trim edge outward, so an edge photo cover-fits into the larger box and
//     the trim cuts through the image. Interior slots are left untouched.
//
// All units are PDF points; callers convert mm → pt before calling.

export interface BleedRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * The full media box for a page printed with bleed. The origin moves negative
 * by `bleedPt` and the size grows by `2 * bleedPt` so the trim rect stays
 * centred inside it. With `bleedPt === 0` this is just the bare trim rect.
 */
export function fullBleedRect(trimW: number, trimH: number, bleedPt: number): BleedRect {
  // `bleedPt ? -bleedPt : 0` avoids producing -0 when bleed is 0, which would
  // read oddly in a media-box origin and trips strict equality in tests.
  return {
    x: bleedPt ? -bleedPt : 0,
    y: bleedPt ? -bleedPt : 0,
    w: trimW + 2 * bleedPt,
    h: trimH + 2 * bleedPt,
  };
}

/**
 * Extend every slot that touches a trim edge outward by `bleedPt` on that edge
 * only. A full-page slot grows on all four sides; a 2-up left cell grows
 * left/top/bottom but keeps its inner (right) edge; an interior slot that
 * touches no trim edge is returned unchanged.
 *
 * Non-geometry fields (rotation, clip_path, …) are preserved. The input array
 * and its elements are never mutated. With `bleedPt <= 0` the original array
 * reference is returned as a no-op.
 *
 * A small tolerance (EPS) absorbs sub-pixel rounding so a slot whose edge lands
 * a fraction of a point short of the trim still counts as touching it.
 */
export function extendSlotsToBleed<T extends { x: number; y: number; w: number; h: number }>(
  slots: T[],
  bleedPt: number,
  trimW: number,
  trimH: number,
): T[] {
  if (bleedPt <= 0) return slots;

  const EPS = 0.5;

  return slots.map((s) => {
    const atLeft = s.x <= EPS;
    const atRight = s.x + s.w >= trimW - EPS;
    const atBottom = s.y <= EPS;
    const atTop = s.y + s.h >= trimH - EPS;

    let { x, y, w, h } = s;
    if (atLeft) {
      x -= bleedPt;
      w += bleedPt;
    }
    if (atRight) {
      w += bleedPt;
    }
    if (atBottom) {
      y -= bleedPt;
      h += bleedPt;
    }
    if (atTop) {
      h += bleedPt;
    }

    return { ...s, x, y, w, h };
  });
}

type DrawRect = { x: number; y: number; width: number; height: number };
type SlotRect = { x: number; y: number; w: number; h: number };

/**
 * EDITOR = PRINT for every photo, the bleed only FILLS (#1236, 2026-10-14).
 *
 * The draw rect is computed on the TRIM slot — the exact box the editor shows.
 * Before, it was computed on the bleed-extended slot, so the customer's own
 * zoom-out (scale 0.74 / 0.51 on #1236 p.27) printed ~3 % bigger and shifted,
 * and the caption they had placed beside the photo landed ON it.
 *
 * Then:
 *  - a photo that COVERS its trim slot is grown about the slot centre just
 *    enough to reach the bleed edges (0 when its own overflow already does) and
 *    clipped to the bleed slot — no white sliver at the cut, nothing moves on
 *    the trimmed page beyond that ≤ bleed/slot growth;
 *  - a photo that does NOT cover it (zoomed out, letterboxed) keeps the
 *    editor's rect exactly and is clipped to the TRIM slot, like the editor.
 */
export function bleedPhotoPlacement(
  win: DrawRect, full: DrawRect, trim: SlotRect, bleed: SlotRect,
): { draw: DrawRect; clip: SlotRect } {
  const EPS = 0.5;
  const covers = win.x <= trim.x + EPS && win.y <= trim.y + EPS
    && win.x + win.width >= trim.x + trim.w - EPS && win.y + win.height >= trim.y + trim.h - EPS;
  if (!covers) return { draw: full, clip: trim };
  // Grow toward the CUT only: anchored on the slot's inner edge when one side
  // needs bleed, on its centre when both do. Scaling about the centre made a
  // corner photo 8 % bigger to win 3 mm at one edge (#1236 p.28).
  const E = 1e-6;
  const anchor = (lo: boolean, hi: boolean, tLo: number, tHi: number) =>
    lo && hi ? (tLo + tHi) / 2 : lo ? tHi : hi ? tLo : (tLo + tHi) / 2;
  const needL = win.x > bleed.x + E, needR = win.x + win.width < bleed.x + bleed.w - E;
  const needB = win.y > bleed.y + E, needT = win.y + win.height < bleed.y + bleed.h - E;
  if (!needL && !needR && !needB && !needT) return { draw: full, clip: bleed };
  const ax = anchor(needL, needR, trim.x, trim.x + trim.w);
  const ay = anchor(needB, needT, trim.y, trim.y + trim.h);
  const ratio = (to: number, from: number, a: number) => (Math.abs(from - a) < E ? 1 : (to - a) / (from - a));
  const s = Math.max(1,
    needL ? ratio(bleed.x, win.x, ax) : 1,
    needR ? ratio(bleed.x + bleed.w, win.x + win.width, ax) : 1,
    needB ? ratio(bleed.y, win.y, ay) : 1,
    needT ? ratio(bleed.y + bleed.h, win.y + win.height, ay) : 1,
  );
  return {
    draw: { x: ax + (full.x - ax) * s, y: ay + (full.y - ay) * s, width: full.width * s, height: full.height * s },
    clip: bleed,
  };
}
